import { createBrowserHistory, parsePath } from '@remix-run/router';
import { EDU_BASENAME, isEduPrefixedPath } from '../config/appBase';

/**
 * Browser history for hosts whose root is the Edu home (EDU_ROOT_HOSTS).
 *
 * The router runs under the /edu basename, so it only matches paths that
 * start with /edu. This history reports browser "/" to the router as "/edu"
 * (router "/", the Edu home) and writes router "/edu" back to the browser as
 * "/". Every other path passes through unchanged: ivoriai.com/edu/lnat is
 * still /edu/lnat on both sides.
 *
 * <Link to="/">, navigate('/') and <Navigate to="/"> therefore land on
 * ivoriai.com/, and links from the home into the app keep their /edu path.
 */

/** Browser path -> router path: "/" becomes "/edu". */
export function toRouterPathname(pathname) {
  return pathname === '/' || pathname === '' ? EDU_BASENAME : pathname;
}

/** Router path -> browser path: "/edu" and "/edu/" become "/". */
export function toBrowserPathname(pathname) {
  if (typeof pathname !== 'string' || !isEduPrefixedPath(pathname)) return pathname;
  const rest = pathname.slice(EDU_BASENAME.length);
  return rest === '' || rest === '/' ? '/' : pathname;
}

function toBrowserTo(to) {
  const path = typeof to === 'string' ? parsePath(to) : { ...to };
  if (path.pathname !== undefined) path.pathname = toBrowserPathname(path.pathname);
  return path;
}

function toRouterLocation(location) {
  const pathname = toRouterPathname(location.pathname);
  return pathname === location.pathname ? location : { ...location, pathname };
}

export function createRootAliasHistory(options = {}) {
  const history = createBrowserHistory({ v5Compat: true, ...options });
  return {
    get action() {
      return history.action;
    },
    get location() {
      return toRouterLocation(history.location);
    },
    createHref: (to) => history.createHref(toBrowserTo(to)),
    createURL: (to) => history.createURL(toBrowserTo(to)),
    // Encoding only; the path stays in router space for matching.
    encodeLocation: (to) => history.encodeLocation(to),
    push: (to, state) => history.push(toBrowserTo(to), state),
    replace: (to, state) => history.replace(toBrowserTo(to), state),
    go: (delta) => history.go(delta),
    listen: (listener) => history.listen((update) => listener({
      ...update,
      location: toRouterLocation(update.location),
    })),
  };
}
